import type { ReferralSummary, ReferralTrendPoint } from '@/services/database';

const DAY_MS = 24 * 60 * 60 * 1_000;
const referralIntervalDays: Record<ReferralSummary['analytics']['interval'], number> = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365,
    all: 365,
};

interface ResolveReferralSparklineOptions {
    analytics?: ReferralSummary['analytics'];
    now?: Date;
    points?: ReferralTrendPoint[];
    value?: number | null;
}

const resolveBucketSize = (startTime: number, endTime: number) => {
    const durationDays = Math.max(1, Math.ceil((endTime - startTime) / DAY_MS));

    if (durationDays <= 90) {
        return DAY_MS;
    }
    if (durationDays <= 366) {
        return 7 * DAY_MS;
    }

    return Math.max(DAY_MS, Math.ceil(durationDays / 52) * DAY_MS);
};

const createRollingBuckets = (startTime: number, endTime: number, bucketSize: number): ReferralTrendPoint[] => {
    const buckets: ReferralTrendPoint[] = [];

    for (let bucketTime = startTime; bucketTime < endTime; bucketTime += bucketSize) {
        buckets.push({ date: new Date(bucketTime).toISOString(), value: 0 });
    }
    buckets.push({ date: new Date(endTime).toISOString(), value: 0 });

    return buckets;
};

export const resolveReferralSparkline = ({
    analytics,
    now = new Date(),
    points = [],
    value,
}: ResolveReferralSparklineOptions): ReferralTrendPoint[] => {
    if (!analytics) {
        return points;
    }

    const parsedEndTime = analytics.endDate ? Date.parse(analytics.endDate) : Number.NaN;
    const endTime = Number.isFinite(parsedEndTime) ? parsedEndTime : now.getTime();
    const parsedStartTime = analytics.startDate ? Date.parse(analytics.startDate) : Number.NaN;
    const startTime = Number.isFinite(parsedStartTime)
        ? parsedStartTime
        : endTime - referralIntervalDays[analytics.interval] * DAY_MS;

    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || startTime >= endTime) {
        return points;
    }

    const bucketSize = resolveBucketSize(startTime, endTime);
    const buckets = createRollingBuckets(startTime, endTime, bucketSize);
    let observedTotal = 0;

    points.forEach((point) => {
        const pointTime = Date.parse(point.date);
        const pointValue = Number.isFinite(point.value) ? Math.max(0, point.value) : 0;

        if (!Number.isFinite(pointTime) || pointTime < startTime || pointTime > endTime) {
            return;
        }

        const bucketIndex = Math.min(buckets.length - 1, Math.floor((pointTime - startTime) / bucketSize));
        buckets[bucketIndex].value += pointValue;
        observedTotal += pointValue;
    });

    const total = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
    buckets[buckets.length - 1].value += Math.max(0, total - observedTotal);

    return buckets;
};
